import type { ComponentPropsWithRef, ReactNode } from "react";
import { cx } from "../../lib/cx";
import { Spinner } from "../Spinner/Spinner";
import styles from "./Button.module.css";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ComponentPropsWithRef<"button"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner, keeps the width, and blocks activation. */
  loading?: boolean;
  /** Icon before the label (decorative). */
  icon?: ReactNode;
  fullWidth?: boolean;
}

/**
 * Primary: the one main action per view. Secondary: everything else.
 * Ghost: low-emphasis actions in dense UI (toolbars, table rows).
 * Danger: destructive actions, always behind a confirmation.
 */
export function Button({
  variant = "secondary",
  size = "md",
  loading = false,
  icon,
  fullWidth = false,
  disabled,
  className,
  children,
  type = "button",
  onClick,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      className={cx(styles.button, className)}
      data-variant={variant}
      data-size={size}
      data-full-width={fullWidth || undefined}
      data-loading={loading || undefined}
      // aria-disabled keeps a loading button focusable (focus doesn't jump away).
      aria-disabled={loading || undefined}
      aria-busy={loading || undefined}
      disabled={disabled}
      onClick={loading ? (event) => event.preventDefault() : onClick}
    >
      {loading ? (
        <Spinner size="sm" className={styles.spinner} />
      ) : (
        icon && (
          <span className={styles.icon} aria-hidden="true">
            {icon}
          </span>
        )
      )}
      <span className={styles.label}>{children}</span>
    </button>
  );
}
